/**
 * useRBAC.ts - Role-Based Access Control React Hook
 *
 * FERP Hook Pattern - React integration for RBAC operations
 *
 * GitHub Issue: #336
 * Week: 2 (Frontend Integration)
 *
 * FERP Principles:
 * - Data-Driven: All values from backend via RoleRepository
 * - Type-Safe: Full TypeScript type definitions
 * - React Best Practices: Proper hook dependencies and cleanup
 * - Error Handling: Graceful fallbacks with loading states
 *
 * Usage:
 * ```tsx
 * const {
 *   currentRole,
 *   permissions,
 *   hasPermission,
 *   hasAnyPermission,
 *   hasAllPermissions,
 *   isAdmin,
 *   loading,
 *   error
 * } = useRBAC(tenantId);
 * ```
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import { RoleRepository, UserRoleResponse, UserPermissionsResponse } from '../data/repositories/RoleRepository';

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseRBACReturn {
  // Current role data
  currentRole: string | null;
  roleData: UserRoleResponse | null;

  // Permissions
  permissions: string[];
  scopes: string[];
  capabilities: string[];

  // Permission checks
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasScope: (scope: string) => boolean;
  hasAnyScope: (scopes: string[]) => boolean;
  hasAllScopes: (scopes: string[]) => boolean;
  hasCapability: (capability: string) => boolean;

  // Role checks
  isAdmin: boolean;
  isApprover: boolean;
  isCurator: boolean;
  isOperator: boolean;
  isAnalyst: boolean;
  isViewer: boolean;

  // State
  loading: boolean;
  error: Error | null;

  // Actions
  refresh: () => Promise<void>;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * useRBAC Hook
 *
 * Provides RBAC context for current user within a tenant
 *
 * @param tenantId - Current tenant ID
 * @param options - Hook configuration options
 * @returns RBAC context with permissions and role checks
 */
export function useRBAC(
  tenantId: string,
  options?: {
    /**
     * Skip fetching (useful for conditional rendering)
     */
    skip?: boolean;

    /**
     * Polling interval (ms) for real-time permission updates
     * Default: null (no polling)
     */
    pollInterval?: number;

    /**
     * Callback when role changes
     */
    onRoleChange?: (role: string) => void;

    /**
     * Callback when permissions change
     */
    onPermissionsChange?: (permissions: string[]) => void;
  }
): UseRBACReturn {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(
    () => new RoleRepository(apolloClient),
    [apolloClient]
  );

  // State
  const [roleData, setRoleData] = useState<UserRoleResponse | null>(null);
  const [permissionsData, setPermissionsData] = useState<UserPermissionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(!options?.skip);
  const [error, setError] = useState<Error | null>(null);

  // Ref to track previous role without causing re-renders
  const previousRoleRef = useRef<string | null>(null);

  /**
   * Fetch current user's role and permissions
   */
  const fetchRBACData = useCallback(async () => {
    if (options?.skip || !tenantId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch role
      const role = await repository.getCurrentUserRole(tenantId);
      setRoleData(role);

      // Notify role change using ref (avoids dependency loop)
      if (options?.onRoleChange && role.role !== previousRoleRef.current) {
        options.onRoleChange(role.role);
        previousRoleRef.current = role.role;
      }

      // Fetch permissions
      const permissions = await repository.getUserPermissions(tenantId, role.role);
      setPermissionsData(permissions);

      // Notify permissions change
      if (options?.onPermissionsChange) {
        options.onPermissionsChange(permissions.permissions);
      }

    } catch (err) {
      console.warn('[useRBAC] Failed to fetch RBAC data, using fallback viewer role:', err);
      // Don't set error state since we have a valid fallback
      setError(null);

      // Graceful fallback to viewer role
      setRoleData({
        role: 'viewer',
        tenant: tenantId,
        scopes: ['workflow:read', 'wfdef:read', 'rules:read', 'forms:read'],
        capabilities: ['view_workflows', 'view_templates']
      });
      setPermissionsData({
        permissions: ['workflow:read', 'wfdef:read', 'rules:read', 'forms:read'],
        tenant: tenantId,
        role: 'viewer',
        computedAt: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, repository, options?.skip, options?.onRoleChange, options?.onPermissionsChange]);

  /**
   * Initial fetch
   */
  useEffect(() => {
    fetchRBACData();
  }, [fetchRBACData]);

  /**
   * Polling (if enabled)
   */
  useEffect(() => {
    if (!options?.pollInterval || options.skip) {
      return undefined;
    }

    const interval = setInterval(() => {
      fetchRBACData();
    }, options.pollInterval);

    return () => clearInterval(interval);
  }, [options?.pollInterval, options?.skip, fetchRBACData]);

  // ==========================================================================
  // Derived Values
  // ==========================================================================

  const currentRole = roleData?.role || null;
  const permissions = useMemo(() => permissionsData?.permissions || [], [permissionsData?.permissions]);
  const scopes = useMemo(() => roleData?.scopes || [], [roleData?.scopes]);
  const capabilities = useMemo(() => roleData?.capabilities || [], [roleData?.capabilities]);

  // ==========================================================================
  // Permission Checks
  // ==========================================================================

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return permissions.includes(permission) || scopes.includes(permission);
    },
    [permissions, scopes]
  );

  const hasAnyPermission = useCallback(
    (perms: string[]): boolean => {
      return perms.some((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const hasAllPermissions = useCallback(
    (perms: string[]): boolean => {
      return perms.every((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const hasScope = useCallback(
    (scope: string): boolean => {
      return scopes.includes(scope);
    },
    [scopes]
  );

  const hasAnyScope = useCallback(
    (scopesToCheck: string[]): boolean => {
      return scopesToCheck.some((s) => hasScope(s));
    },
    [hasScope]
  );

  const hasAllScopes = useCallback(
    (scopesToCheck: string[]): boolean => {
      return scopesToCheck.every((s) => hasScope(s));
    },
    [hasScope]
  );

  const hasCapability = useCallback(
    (capability: string): boolean => {
      return capabilities.includes(capability);
    },
    [capabilities]
  );

  // ==========================================================================
  // Role Checks
  // ==========================================================================

  const isAdmin = useMemo(
    () => currentRole === 'admin' || hasScope('tenant:admin'),
    [currentRole, hasScope]
  );

  const isApprover = useMemo(
    () => currentRole === 'approver' || isAdmin,
    [currentRole, isAdmin]
  );

  const isCurator = useMemo(
    () => currentRole === 'curator' || isApprover,
    [currentRole, isApprover]
  );

  const isOperator = useMemo(
    () => currentRole === 'operator' || hasAnyScope(['workflow:append', 'workflow:signal']),
    [currentRole, hasAnyScope]
  );

  const isAnalyst = useMemo(
    () => currentRole === 'analyst' || isCurator,
    [currentRole, isCurator]
  );

  const isViewer = useMemo(
    () => currentRole === 'viewer' || hasScope('workflow:read'),
    [currentRole, hasScope]
  );

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    // Current role data
    currentRole,
    roleData,

    // Permissions
    permissions,
    scopes,
    capabilities,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasScope,
    hasAnyScope,
    hasAllScopes,
    hasCapability,

    // Role checks
    isAdmin,
    isApprover,
    isCurator,
    isOperator,
    isAnalyst,
    isViewer,

    // State
    loading,
    error,

    // Actions
    refresh: fetchRBACData
  };
}

export default useRBAC;
